import React, { useEffect, useState } from "react";
import { Alert, Card, CardContent, Grid, Stack, Typography } from "@mui/material";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import { Link } from "react-router-dom";

import { listNetworkPortalAssignments, listNetworkPortalOffers } from "../../services/coreNetworkPortal";
import {
  NetworkPortalLayout,
  PortalAuthState,
  PortalLoading,
  StatusChip,
  TalentPortalEmptyState,
  formatDateTime,
  safeArray,
  toTitleCase,
  useNetworkPortalSession,
} from "./networkPortalShared";

export default function NetworkPortalDashboard() {
  const { member, loading, error } = useNetworkPortalSession();
  const [assignments, setAssignments] = useState([]);
  const [offers, setOffers] = useState([]);
  const [pageError, setPageError] = useState("");

  useEffect(() => {
    if (!member) return;
    let active = true;

    async function loadData() {
      try {
        setPageError("");
        const [assignmentResponse, offerResponse] = await Promise.all([
          listNetworkPortalAssignments(),
          listNetworkPortalOffers(),
        ]);
        if (!active) return;
        setAssignments(safeArray(assignmentResponse.assignments));
        setOffers(safeArray(offerResponse.offers));
      } catch (loadError) {
        if (active) {
          setPageError(loadError?.response?.data?.message || loadError.message || "Unable to load portal dashboard.");
        }
      }
    }

    loadData();
    return () => {
      active = false;
    };
  }, [member]);

  if (loading) return <PortalLoading />;
  if (!member) return <PortalAuthState error={error} />;

  const pendingOffers = offers.filter((offer) => ["pending", "sent", "viewed"].includes(String(offer.status || "").toLowerCase()));
  const activeAssignments = assignments.filter((assignment) => !["completed", "declined"].includes(String(assignment.status || "").toLowerCase()));

  const statCards = [
    { label: "Pending offers", value: pendingOffers.length },
    { label: "Total offers", value: offers.length },
    { label: "Active assignments", value: activeAssignments.length },
    { label: "Completed assignments", value: assignments.filter((assignment) => String(assignment.status || "").toLowerCase() === "completed").length },
  ];

  return (
    <NetworkPortalLayout title="Dashboard" subtitle="Review the work currently available to you." member={member}>
      <Stack spacing={3}>
        {pageError ? <Alert severity="error">{pageError}</Alert> : null}
        <Grid container spacing={2}>
          {statCards.map((card) => (
            <Grid item xs={12} sm={6} md={3} key={card.label}>
              <Card elevation={0} sx={{ border: "1px solid #d9e2ec", borderRadius: 3 }}>
                <CardContent>
                  <Typography variant="overline" color="text.secondary">{card.label}</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>{card.value}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card elevation={0} sx={{ border: "1px solid #d9e2ec", borderRadius: 3, height: "100%" }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Offers</Typography>
                <Stack spacing={2}>
                  {pendingOffers.length ? pendingOffers.slice(0, 5).map((offer) => (
                    <Card key={offer.id} component={Link} to={`/talent/offers/${offer.id}`} elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px solid #edf2f7", textDecoration: "none", color: "inherit" }}>
                      <Stack spacing={0.75}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {offer.workRequest?.title || `Offer #${offer.id}`}
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <StatusChip value={offer.status} />
                          <Typography variant="caption" color="text.secondary">{toTitleCase(offer.workRequest?.requestType)}</Typography>
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                          Due {formatDateTime(offer.workRequest?.deadlineAt)}
                        </Typography>
                      </Stack>
                    </Card>
                  )) : (
                    <TalentPortalEmptyState icon={<LocalOfferIcon fontSize="small" />} title="No active offers" description="No active offers are waiting for you right now." />
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card elevation={0} sx={{ border: "1px solid #d9e2ec", borderRadius: 3, height: "100%" }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Assignments</Typography>
                <Stack spacing={2}>
                  {activeAssignments.length ? activeAssignments.slice(0, 5).map((assignment) => (
                    <Card key={assignment.id} component={Link} to={`/talent/assignments/${assignment.id}`} elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px solid #edf2f7", textDecoration: "none", color: "inherit" }}>
                      <Stack spacing={0.75}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {assignment.workRequest?.title || `Assignment #${assignment.id}`}
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <StatusChip value={assignment.status} />
                          <Typography variant="caption" color="text.secondary">{toTitleCase(assignment.workRequest?.requestType)}</Typography>
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                          Updated {formatDateTime(assignment.updatedAt)}
                        </Typography>
                      </Stack>
                    </Card>
                  )) : (
                    <TalentPortalEmptyState icon={<AssignmentTurnedInIcon fontSize="small" />} title="No assignments yet" description="New assignments will appear here when Elite Core organizations share work with you." />
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Stack>
    </NetworkPortalLayout>
  );
}
